import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, ViewChild, computed, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AuthService } from 'src/app/auth.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { CourseDisplayerService } from 'src/app/views/displayer/courses/course-displayer/course-displayer.service';
import { ChunkService } from '../../chunk.service';

import { SkeletonComponent } from 'src/app/shared/components/ui/skeleton/skeleton.component';

@Component({
  selector: 'app-video-chunk-displayer',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule, MatChipsModule, SkeletonComponent],
  styles: [
    `
      :host {
        display: block;
      }

      .video-wrapper {
        border-radius: 12px;
        overflow: hidden;
        display: flex;
        justify-content: center;
        background-color: var(--skz-background-color-surface);
      }

      video {
        border-radius: 12px;
        display: block;
        max-height: 60vh;
        width: 100%;
        object-fit: contain;
      }

      /* ─── Description Card ─────────────────────── */
      .video-description-card {
        position: relative;
        margin-top: 8px;
        border-radius: var(--skz-radius-main);
        border: 1px solid var(--skz-border-color-main);
        background: var(--skz-background-color-surface);
        overflow: hidden;
      }

      .description-preview-area {
        position: relative;
        overflow: hidden;
      }

      .description-text {
        padding: 12px 16px;
        font-size: 13px;
        line-height: 1.65;
        color: var(--skz-text-primary);
        white-space: pre-wrap;
        word-break: break-word;

        &.expandable:not(.expanded) {
          max-height: 80px;
          overflow: hidden;
        }

        &.expanded {
          max-height: 300px;
          padding-bottom: 3rem;
          overflow-y: auto;
        }
      }

      /* ─── Expansion Overlay ────────────────────── */
      .expansion-overlay {
        position: absolute;
        inset-inline: 0;
        bottom: 0;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        height: 48px;
        padding-bottom: 8px;
        background: linear-gradient(to top, var(--skz-background-color-surface), rgba(255, 255, 255, 0.7), transparent);
        transition: all 0.3s ease;
        pointer-events: none;
      }

      .show-more-pill {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 0 4px 0 12px;
        border-radius: 12px;
        border: 1px solid var(--skz-border-color-main);
        background: var(--skz-background-color-surface);
        backdrop-filter: blur(8px);
        font-size: 11px;
        font-weight: 600;
        color: var(--skz-text-muted);
        box-shadow: var(--skz-box-shadow-light);
        transition: all 0.25s ease;
        pointer-events: auto;
        cursor: pointer;

        .show-more-icon {
          font-size: 16px;
          width: 16px;
          height: 16px;
          transition: transform 0.25s ease;

          &.rotated {
            transform: rotate(180deg);
          }
        }
      }

      .video-description-card:hover .show-more-pill {
        border-color: rgba(90, 115, 197, 0.3);
        color: var(--skz-icon-color-blue);
        box-shadow: 0 2px 8px rgba(90, 115, 197, 0.12);
      }

      /* ─── Inline Description Edit ──────────────── */
      .description-text {
        &.editable {
          cursor: pointer;
        }
      }

      .edit-desc-container {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 8px 12px;
        background: var(--skz-background-color-surface);
        border-radius: var(--skz-radius-low);
        box-shadow: 0 0 0 1px rgba(90, 115, 197, 0.1);
      }

      .desc-edit-textarea {
        flex: 1;
        min-height: 80px;
        resize: vertical;
        padding: 4px 4px;
        font-size: 13px;
        line-height: 1.65;
        font-weight: 400;
        color: var(--skz-text-primary);
        background: transparent;
        border: none;
        outline: none;
        font-family: inherit;
        box-sizing: border-box;
      }

      .desc-edit-actions {
        display: flex;
        justify-content: flex-end;
        gap: 4px;
      }

      .edit-action-btn {
        flex-shrink: 0;
        width: 24px !important;
        height: 24px !important;
        line-height: 24px !important;
        padding: 0 !important;
        transition:
          background 0.15s ease,
          color 0.15s ease;

        .mat-icon {
          font-size: 16px !important;
          width: 16px !important;
          height: 16px !important;
        }

        &.confirm-btn {
          color: var(--skz-icon-color-blue);
          &:hover {
            background: var(--skz-highlighted-item-color-lighter);
          }
        }

        &.cancel-btn {
          color: var(--skz-text-muted);
          &:hover {
            background: rgba(0, 0, 0, 0.06);
          }
        }
      }

      /* ─── Tags Row ──────────────────────────────── */
      .tags-row {
        padding: 0 16px 12px;

        mat-chip.clickable-tag {
          cursor: pointer;
          transition: filter 0.2s ease;

          &:hover {
            filter: brightness(0.95);
          }
        }

        .timestamp-label {
          font-size: 11px;
          font-weight: 500;
          color: var(--skz-text-muted);
          margin-left: 4px;
        }
      }
    `,
  ],
  template: `
    <div class="video-wrapper" (click)="onUserInteraction()">
      @if (chunk().video) {
        @if (isVideoLoading()) {
          <ui-skeleton type="video" [noPadding]="true" style="width: 100%;"></ui-skeleton>
        }
        <video
          #videoPlayer
          controls
          height="100%"
          width="100%"
          controlsList="nodownload"
          [src]="chunk().video"
          (ended)="onVideoEnded()"
          (play)="onPlay()"
          (pause)="onPause()"
          (loadstart)="onLoadStart()"
          (loadeddata)="onLoadedData()"
          (canplay)="onCanPlay()"
          (playing)="onPlaying()"
          [style.display]="isVideoLoading() ? 'none' : 'block'"
        ></video>
      }
    </div>

    @if (chunk().videoDescription || chunk().tags?.length) {
      <div class="video-description-card">
        <!-- Description Preview / Inline Edit -->
        <div class="description-preview-area">
          @if (isEditingDesc()) {
            <!-- Inline Edit (transcript-style) -->
            <div class="edit-desc-container" (click)="$event.stopPropagation()">
              <textarea #descTextarea class="desc-edit-textarea" [value]="tempDesc()" (keydown.escape)="cancelDescEdit()"></textarea>
              <div class="desc-edit-actions">
                <button mat-icon-button class="edit-action-btn cancel-btn" (click)="cancelDescEdit()" [matTooltip]="'Global.Cancel' | translate">
                  <mat-icon>close</mat-icon>
                </button>
                <button mat-icon-button class="edit-action-btn confirm-btn" (click)="saveDescEdit(descTextarea.value)" [matTooltip]="'Global.Save' | translate">
                  <mat-icon>check</mat-icon>
                </button>
              </div>
            </div>
          } @else {
            <div
              class="description-text"
              [class.expandable]="isExpandable()"
              [class.expanded]="isExpanded()"
              [class.editable]="editMode()"
              (click)="editMode() && startDescEdit($event)"
            >
              {{ chunk().videoDescription }}
            </div>

            <!-- Expansion Overlay -->
            @if (isExpandable()) {
              <div class="expansion-overlay">
                <div class="show-more-pill" (click)="$event.stopPropagation(); toggleExpand()">
                  <span>{{ (isExpanded() ? 'Global.ShowLess' : 'Global.ShowMore') | translate }}</span>
                  <mat-icon class="show-more-icon" [class.rotated]="isExpanded()">expand_more</mat-icon>
                </div>
              </div>
            }
          }
        </div>

        <!-- Tags Row (visible only when expanded) -->
        @if (isExpanded() && chunk().tags?.length) {
          <div class="tags-row">
            <mat-chip-set>
              @for (tag of chunk().tags; track tag) {
                <mat-chip [class.clickable-tag]="!!tag.timestamp" (click)="tag.timestamp ? playAtTimestamp(tag.timestamp) : null">
                  {{ tag.content }}
                  @if (tag.timestamp) {
                    <span class="timestamp-label">({{ tag.timestamp }})</span>
                  }
                </mat-chip>
              }
            </mat-chip-set>
          </div>
        }
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoChunkDisplayerComponent {
  chunk = input.required<any>();
  highlightedChunk = input<string>();

  private authService = inject(AuthService);
  private cdService = inject(CourseDisplayerService);
  private chunkService = inject(ChunkService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private contentStateService = inject(ContentStateService);
  private destroyRef = inject(DestroyRef);

  @ViewChild('videoPlayer') videoPlayer?: ElementRef<HTMLVideoElement>;

  @ViewChild('descTextarea') set descTextarea(el: ElementRef<HTMLTextAreaElement>) {
    if (el) {
      const ta = el.nativeElement;
      ta.focus();
      ta.setSelectionRange(ta.value.length, ta.value.length);
    }
  }

  loading = false;
  isVideoLoading = signal(true);

  onLoadStart() {
    this.isVideoLoading.set(true);
  }

  onLoadedData() {
    this.isVideoLoading.set(false);
  }

  onCanPlay() {
    this.isVideoLoading.set(false);
  }

  onPlaying() {
    this.isVideoLoading.set(false);
  }
  private wasProgrammaticallyPlayed = false;

  isExpanded = signal(false);
  isEditingDesc = signal(false);
  tempDesc = signal('');

  isWriter = computed(() => this.authService.isWriter() && !this.contentStateService.isReader());
  editMode = this.isWriter; // alias kept for template clarity
  playbackRate = toSignal(this.cdService.audioSpeed$);

  isHighlighted = computed(() => this.highlightedChunk() === this.chunk()._id);

  isExpandable = computed(() => {
    const desc = this.chunk()?.videoDescription || '';
    const hasTags = (this.chunk()?.tags?.length ?? 0) > 0;
    return hasTags || desc.length > 200 || desc.split('\n').length > 4;
  });

  constructor() {
    effect(() => {
      const command = this.contentStateService.playbackCommand();
      if (command?.chunkId === this.chunk()._id) {
        const player = this.videoPlayer?.nativeElement;
        if (!player) return;
        if (command.action === 'play' && player.paused) {
          this.wasProgrammaticallyPlayed = true;
          player.play();
        } else if (command.action === 'pause' && !player.paused) {
          player.pause();
        }
      }
    });

    effect(() => {
      const rate = this.playbackRate();
      const player = this.videoPlayer?.nativeElement;
      if (player && rate) {
        player.playbackRate = rate;
      }
    });
  }

  toggleExpand(): void {
    this.isExpanded.update((v) => !v);
  }

  onVideoEnded(): void {
    if (this.wasProgrammaticallyPlayed) {
      this.contentStateService.handleAudioEnded();
    }
    this.wasProgrammaticallyPlayed = false;
  }

  onUserInteraction(): void {
    if (this.wasProgrammaticallyPlayed) {
      this.contentStateService.clearPlaybackCommand();
    }
    this.wasProgrammaticallyPlayed = false;
  }

  playAtTimestamp(timestamp: string): void {
    const player = this.videoPlayer?.nativeElement;
    if (!player) return;

    const parts = timestamp.split(':').map(Number);
    let seconds = 0;
    if (parts.length === 3) {
      seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      seconds = parts[0] * 60 + parts[1];
    } else if (parts.length === 1) {
      seconds = parts[0];
    }

    player.currentTime = seconds;
    player.play().catch((err) => console.error('Error playing video:', err));
  }

  onPlay(): void {
    this.contentStateService.reportPlaybackStarted(this.chunk()._id);
  }

  onPause(): void {
    this.contentStateService.reportPlaybackPaused(this.chunk()._id);
  }

  startDescEdit(event: MouseEvent): void {
    if (!this.editMode()) return;
    event.stopPropagation();
    this.tempDesc.set(this.chunk().videoDescription || '');
    this.isEditingDesc.set(true);
  }

  cancelDescEdit(): void {
    this.isEditingDesc.set(false);
  }

  saveDescEdit(newDesc: string): void {
    if (!this.editMode()) return;
    const trimmed = newDesc.trim();
    if (trimmed === (this.chunk().videoDescription || '').trim()) {
      this.isEditingDesc.set(false);
      return;
    }

    this.loading = true;
    this.chunkService
      .editVideoDescription({
        id: this.chunk()._id,
        videoDescription: trimmed,
      })
      .subscribe({
        next: () => {
          this.chunk().videoDescription = trimmed;
          this.isEditingDesc.set(false);
          this.loading = false;
        },
        error: (err) => {
          console.error(err);
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', { duration: 2000 });
          this.loading = false;
        },
      });
  }
}
